import { LngLat } from "maplibre-gl";

import { removeNullUndefined } from "@nens/shared-utils";

import { useClickedOnVectorMap } from "@/hooks/use-clicked-on-vector-map";
import { FileDataTypeEnum } from "@/lib/rana-file-data-type";
import {
  DisplayPopupInfo,
  DisplayPopupInfoItem,
} from "@/lib/rana-popup/components/display-popup-info";
import { RanaPopup } from "@/lib/rana-popup/rana-popup";
import { ScenarioFileDescriptor, VectorFileDescriptor } from "@/types/descriptor-types";
import { getLegendUnit } from "@/utils/get-legend-unit";

import { getFileByPath, getFiles, getVisibleLayersOfType } from "../../store/selectors";
import { usePublicationStore } from "../../store/store";
import { CompleteFile } from "../../types/file-reference-with-file";
import { Layer, RasterLayer } from "../../types/publication";
import { PUBLICATION_MAP_ID } from "../map/utils/map-id";

import { usePublicationVectorStylingOptions } from "./hooks/use-vector-styling-options";
import { getScenarioLayerByCode } from "./utils/get-scenario-layer-by-code";

export const PublicationPopup = () => {
  const visibleScenarioLayers = usePublicationStore(
    getVisibleLayersOfType(FileDataTypeEnum.Scenario)
  );
  const visibleRasterLayers = usePublicationStore(getVisibleLayersOfType(FileDataTypeEnum.Raster));

  const visibleVectorLayers = usePublicationStore(getVisibleLayersOfType(FileDataTypeEnum.Vector));
  const files = usePublicationStore(getFiles);
  const visibleVectorDescriptorLayers = removeNullUndefined(
    visibleVectorLayers.map((layer) => {
      const file = files.find((file) => file.path === layer.filePath);
      if (!file) return null;
      return { fileDescriptor: file.file.descriptor as VectorFileDescriptor, layer };
    })
  );
  const vectorLayers = usePublicationVectorStylingOptions(visibleVectorDescriptorLayers) ?? [];

  const { clearMapClick, point, setPoint, clickedPoint, vectorInfoItems } = useClickedOnVectorMap(
    PUBLICATION_MAP_ID,
    vectorLayers
  );

  const vectorPopupItems: DisplayPopupInfoItem[] = vectorInfoItems.map((item) => ({
    title: item.title,
    features: item.features,
    descriptorId: item.descriptorId,
    layerName: item.layerName,
    isVector: true as const,
  }));

  // Only show popup when there is something to show
  if (
    !point ||
    !clickedPoint ||
    (!vectorInfoItems.length && !visibleRasterLayers.length && !visibleScenarioLayers.length)
  ) {
    return null;
  }

  const items: DisplayPopupInfoItem[] = [
    ...vectorPopupItems,
    ...visibleRasterLayers.map((layer) => ({
      title: layer.name,
      children: <RasterLayerPopupInfo key={layer.id} layer={layer} point={point} />,
      isVector: false as const,
    })),
    ...visibleScenarioLayers.map((layer) => ({
      title: layer.name,
      children: <ScenarioLayerPopupInfo key={layer.id} layer={layer} point={point} />,
      isVector: false as const,
    })),
  ];

  return (
    <RanaPopup point={point} onClose={clearMapClick}>
      <DisplayPopupInfo hasIcons items={items} setPoint={setPoint} clickedPoint={clickedPoint} />
    </RanaPopup>
  );
};

function ScenarioLayerPopupInfo({ layer, point }: { layer: Layer; point: LngLat }) {
  const file: CompleteFile | null = usePublicationStore(getFileByPath(layer.filePath));

  if (!file || !layer.layerInFile) return null;

  const descriptor = file.file.descriptor as ScenarioFileDescriptor;
  if (!descriptor.meta) return null;

  const scenarioLayer = getScenarioLayerByCode(descriptor.meta.layers, layer.layerInFile);

  if (!scenarioLayer?.id) return null;

  return (
    <RanaPopup.ScenarioInfo
      layers={[scenarioLayer]}
      descriptorId={file.file.descriptorId}
      point={point}
    />
  );
}

function RasterLayerPopupInfo({ layer, point }: { layer: RasterLayer; point: LngLat }) {
  const file: CompleteFile | null = usePublicationStore(getFileByPath(layer.filePath));

  if (!file) return null;

  const descriptor = file.file.descriptor;
  const unit = getLegendUnit(descriptor);

  return (
    <RanaPopup.RasterInfo
      styleId={layer.properties.styleId ?? ""}
      layer={layer.id}
      unit={unit}
      descriptorId={file.file.descriptorId}
      point={point}
    />
  );
}
